import { useSelector } from 'react-redux';
import { useMemo, useState, useEffect } from 'react';

import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectEnableInhouseJackpot, selectIsGermanRegulatedFranchise } from '~/components/brand/ducks/selectors';
import { currentGameSelector, enableMultiGameSelector } from '~/components/casino-shared/game-loader/ducks/selectors';
import { UnauthenticatedHeader } from '~/components/header/unauthenticated-header';
import LoginLink from '~/components/header/login-link';
import UserDetails from '~/components/header/user-details.js';
import { Bigger, isSmallerSelector } from '~/components/layout/mediaquery';
import InhouseJackpotButton from '~/components/casino-shared/inhouse-jackpot/inhouse-jackpot-button.js';
import { TournamentRankButton } from '~/components/casino-shared/tournaments/async';
import useIsTournamentRankAvailable from '~/components/casino-shared/tournaments/utils/use-is-tournament-rank-available';
import PageHeadRightSide from './pagehead-right-side.js';
import ButtonRegister from './button-register.js';
import IconText from '../footer/footer-logo-text';
import {
	selectOneBankIDFlowEnabled,
	selectOneButtonFlowEnabled
} from '~/store/feature-toggles/onboarding-feature-toggles';
import AddGameButton from '~/components/casino-shared/game-loader/multi-game/add-game-button.js';
import { useCasinoSharedSelector } from '~/components/casino-shared/ducks/types';

const RightContainerHeader = ({ isInGame }) => {
	const oneBankIDFlowEnabled = useSelector(selectOneBankIDFlowEnabled);
	const oneButtonFlow = useSelector(selectOneButtonFlowEnabled);
	const isSmaller = useSelector(isSmallerSelector);
	const authenticated = useSelector(authenticationSelector);
	const showLoginLink = (!oneBankIDFlowEnabled && !oneButtonFlow) || isSmaller;
	const isInhouseJackpotEnabled = useSelector(selectEnableInhouseJackpot);
	const currentGame = useSelector(currentGameSelector)?.gameData;
	const showEighteenPlus = useSelector(selectIsGermanRegulatedFranchise);

	const isInhouseJackpotAvailable = useMemo(
		() => isInhouseJackpotEnabled && !!currentGame?.inhouseJackpots?.length && isInGame,
		[isInhouseJackpotEnabled, currentGame?.inhouseJackpots, isInGame]
	);
	const isTournamentRankAvailable = useIsTournamentRankAvailable(currentGame?.code) && !!isInGame;

	const enableMultiGameRaw = useCasinoSharedSelector(state =>
		enableMultiGameSelector(state, {
			isDesktop: true
		})
	);

	// Stable value that only updates once loading is finished AND categoryCode is present.
	const [enableMultiGame, setEnableMultiGame] = useState(enableMultiGameRaw);

	useEffect(() => {
		if (!currentGame?.categoryCode) return;
		// When loading finished and we have a categoryCode, adopt latest raw value.
		setEnableMultiGame(enableMultiGameRaw);
	}, [enableMultiGameRaw, currentGame?.categoryCode]);

	const addGameButtonAvailable = useMemo(() => {
		return isInGame && enableMultiGame;
	}, [isInGame, enableMultiGame]);

	return (
		<PageHeadRightSide isInGame={isInGame}>
			{authenticated ? (
				<>
					{addGameButtonAvailable && <AddGameButton isDesktop />}
					{isInhouseJackpotAvailable && <InhouseJackpotButton isDesktop />}
					{isTournamentRankAvailable && !!currentGame?.code ? (
						<TournamentRankButton gameCode={currentGame.code} />
					) : null}
					<UserDetails isInGame={isInGame} />
				</>
			) : (
				<>
					<Bigger>
						<UnauthenticatedHeader ButtonRegister={ButtonRegister} />
					</Bigger>
					{showLoginLink && <LoginLink />}
					{showEighteenPlus && (
						<IconText
							size="40px"
							marginLeft="30px"
							smaller
							disablePopup
							item={{ name: '18+', url: 'LICENSE_AGE_LIMIT_TEXT' }}
						/>
					)}
				</>
			)}
		</PageHeadRightSide>
	);
};

export default RightContainerHeader;
